import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useBrand } from '../../Brand';
import { useTranslation } from '../../i18n';
import { colors, contentContainerSx, radius } from '../../theme/tokens';
import { ROUTES } from '../../utils/routes';
import type { ComponentProps } from '..';

/**
 * Cabeçalho da marca.
 *
 * Paridade com o legado:
 *
 * - a barra usa a cor primária da marca (`--color-brand-header`);
 * - o **logo só aparece quando a API devolve um**. Marca sem logo cadastrado
 *   no ERP responde `logo_url: ""` e a barra fica sem imagem — mesmo resultado
 *   do `@if(!empty($brand['logo']))` da view Blade;
 * - a ação à direita depende da página: `variables.text[0]` vale
 *   `"change-number"` (Alterar número, na lista de planos) ou `"back"`
 *   (Voltar, no cartão). Sem `variables`, não há ação — é a home.
 */
export function BrandHeader({ variables }: ComponentProps) {
  const { identity } = useBrand();
  const { t } = useTranslation();

  const action = variables?.text?.[0];

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: colors.brandHeader,
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      }}
    >
      <Box
        sx={{
          ...contentContainerSx,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {identity.logoUrl ? (
            <RouterLink to={ROUTES.home} style={{ display: 'inline-flex' }}>
              <Box
                component="img"
                src={identity.logoUrl}
                alt={identity.nomeFantasia}
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  // Logo ausente no ERP: some com a imagem em vez de exibir o
                  // ícone de imagem quebrada (mesmo comportamento do legado).
                  event.currentTarget.style.display = 'none';
                }}
                sx={{ height: 48, width: 'auto', objectFit: 'contain', p: 0.5 }}
              />
            </RouterLink>
          ) : null}
        </Box>

        {action ? (
          <Button
            component={RouterLink}
            to={action === 'back' ? ROUTES.plans : ROUTES.home}
            sx={{
              color: 'rgba(255,255,255,0.9)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: radius.full,
              px: 2,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' },
            }}
          >
            {action === 'back' ? t.common.back : t.plans.changeNumber}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
